import type { ReactNode, MouseEvent } from 'react';
import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './EntityModal.css';

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}

const EntityModal = ({ isOpen, onClose, title, children, footer, width }: EntityModalProps) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className="entity-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div
        className="entity-modal"
        style={width ? { maxWidth: width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="entity-modal-header">
          <h2 className="entity-modal-title">{title}</h2>
          <button className="entity-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="entity-modal-body">{children}</div>
        {footer && <div className="entity-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default EntityModal;
